import { CheckCircle2Icon, GlobeIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DataTable, EmptyState, ErrorNotice, Panel, Pill, SkeletonTable, type DataTableSort, type PillTone } from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { shortDate } from '@/lib/resources';
import { useTableSort } from '@/lib/use-table-sort';
import { PLACEMENT_STATUS, type PartnerPlacement, type PlacementStatus } from '@/lib/types';

/** The partner list's placement filter, kept in the URL (`?placement=pending`). */
export const PLACEMENT_FILTER = { all: 'all', pending: 'pending', verified: 'verified' } as const;
export type PlacementFilter = (typeof PLACEMENT_FILTER)[keyof typeof PLACEMENT_FILTER];
/** A filter that narrows the partners by placement (not All). */
export type PlacementTab = Exclude<PlacementFilter, typeof PLACEMENT_FILTER.all>;

export const PLACEMENT_FILTERS = [
  { key: PLACEMENT_FILTER.all, label: 'All partners' },
  { key: PLACEMENT_FILTER.pending, label: 'Pending' },
  { key: PLACEMENT_FILTER.verified, label: 'Verified' },
] as const;

/**
 * Pending is everyone we cannot count as showing us: never added, taken down,
 * or a page our weekly check could not load.
 */
const FILTER_STATUSES: Readonly<Record<PlacementTab, readonly PlacementStatus[]>> = {
  [PLACEMENT_FILTER.pending]: [PLACEMENT_STATUS.none, PLACEMENT_STATUS.missing, PLACEMENT_STATUS.unreachable],
  [PLACEMENT_FILTER.verified]: [PLACEMENT_STATUS.verified],
};

/** Plain wording per status; `order` sorts the most urgent first. */
const STATUS_DISPLAY: Readonly<Record<PlacementStatus, { label: string; tone: PillTone; order: number }>> = {
  [PLACEMENT_STATUS.missing]: { label: 'Missing from their page', tone: 'danger', order: 0 },
  [PLACEMENT_STATUS.unreachable]: { label: 'Page unreachable', tone: 'warning', order: 1 },
  [PLACEMENT_STATUS.none]: { label: 'Not added yet', tone: 'neutral', order: 2 },
  [PLACEMENT_STATUS.verified]: { label: 'Verified', tone: 'success', order: 3 },
};

/** Sort keys are the flattened row fields `sortRows` reads. */
const PLACEMENT_SORT_FIELDS = {
  name: 'name',
  status: 'statusOrder',
  page: 'url',
  checked: 'checkedAt',
  cities: 'cityList',
} as const;
const SORT_KEYS = Object.values(PLACEMENT_SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: PLACEMENT_SORT_FIELDS.name, direction: 'asc' };

interface PlacementRow extends PartnerPlacement {
  readonly statusOrder: number;
  readonly cityList: string;
}

const SEARCH_FIELDS: ReadonlyArray<keyof PlacementRow> = ['name', 'slug', 'url', 'cityList'];

/**
 * The partners in one placement filter, from the full list.
 *
 * @param rows - Every partner's placement.
 * @param filter - Pending or Verified.
 * @returns The partners in that filter.
 */
export function partnersInFilter(rows: readonly PartnerPlacement[], filter: PlacementTab): PartnerPlacement[] {
  const statuses = FILTER_STATUSES[filter];
  return rows.filter((row) => statuses.includes(row.status));
}

const stripScheme = (url: string): string => url.replace(/^https?:\/\//, '');

export interface PartnerPlacementsTableProps {
  readonly filter: PlacementTab;
  readonly search: string;
}

/**
 * Showcase partners by whether they have put us on their site: the page we
 * check, when we last checked it, and the city pages their card waits on.
 * Their card only shows on our city pages while the check says Verified.
 *
 * @param props - The filter and the page's search.
 * @returns The table, or its loading, empty or error state.
 */
export function PartnerPlacementsTable({ filter, search }: PartnerPlacementsTableProps): React.ReactElement {
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const placements = useQuery({
    queryKey: ['organizations', 'partner-placements'],
    queryFn: ({ signal }) => getJson<PartnerPlacement[]>('/organizations/partner-placements', undefined, signal),
  });

  if (placements.error) return <ErrorNotice title="Partner placements unavailable" message={errorMessage(placements.error)} />;
  if (placements.isPending) {
    return <SkeletonTable columns={['w-[24%]', 'w-[16%]', 'w-[26%]', 'w-[12%]', 'w-[18%]']} label="Loading partner placements…" />;
  }

  const rows: PlacementRow[] = partnersInFilter(placements.data, filter).map((row) => ({
    ...row,
    statusOrder: STATUS_DISPLAY[row.status].order,
    cityList: row.cities.join(', '),
  }));
  const shown = filterRows(sortRows(rows, sort), search, SEARCH_FIELDS);

  if (shown.length === 0) {
    return (
      <Panel>
        {search ? (
          <EmptyState message={`No partners match “${search}”.`} />
        ) : filter === PLACEMENT_FILTER.pending ? (
          <EmptyState
            icon={<CheckCircle2Icon aria-hidden className="size-6" />}
            title="Every partner has us on their site"
            message="Partners land here when they have not added our showcase or a link to us yet, or when our weekly check finds it gone."
          />
        ) : (
          <EmptyState
            icon={<GlobeIcon aria-hidden className="size-6" />}
            title="No verified partners yet"
            message="Partners show up here once our weekly check finds our showcase or a link to us on their page."
          />
        )}
      </Panel>
    );
  }

  const status = (row: PlacementRow): React.ReactElement => (
    <Pill tone={STATUS_DISPLAY[row.status].tone}>{STATUS_DISPLAY[row.status].label}</Pill>
  );
  const page = (row: PlacementRow): React.ReactElement =>
    row.url ? (
      <a href={row.url} target="_blank" rel="noreferrer" className="break-all text-brand underline-offset-2 hover:underline">
        {stripScheme(row.url)}
      </a>
    ) : (
      <span className="text-portal-muted">No page given</span>
    );
  const checked = (row: PlacementRow): string => (row.checkedAt ? shortDate(row.checkedAt) : 'Never');
  const cities = (row: PlacementRow): string => row.cityList || 'None';

  return (
    <DataTable
      guide="partner-placements-table"
      headers={[
        { label: 'Partner', sortKey: PLACEMENT_SORT_FIELDS.name },
        { label: 'Their site', sortKey: PLACEMENT_SORT_FIELDS.status },
        { label: 'Page we check', sortKey: PLACEMENT_SORT_FIELDS.page },
        { label: 'Last checked', sortKey: PLACEMENT_SORT_FIELDS.checked },
        { label: 'City pages', sortKey: PLACEMENT_SORT_FIELDS.cities },
      ]}
      minWidth={860}
      sort={sort}
      onSort={onSort}
      cards={shown.map((row) => ({
        key: row.organizationId,
        title: <Link to={`/organizations/${row.organizationId}`}>{row.name}</Link>,
        subtitle: row.slug,
        fields: [
          { label: 'Their site', value: status(row) },
          { label: 'Last checked', value: checked(row) },
          { label: 'Page we check', value: page(row) },
          { label: 'City pages', value: cities(row) },
        ],
      }))}
    >
      {shown.map((row) => (
        <tr key={row.organizationId} className="border-t border-portal-line align-middle">
          <td className="p-3">
            <Link to={`/organizations/${row.organizationId}`} className="text-portal-small font-bold underline-offset-2 hover:underline">
              {row.name}
            </Link>
            <div className="text-portal-micro text-portal-muted">{row.slug}</div>
          </td>
          <td className="p-3 whitespace-nowrap">{status(row)}</td>
          <td className="p-3 text-portal-small">{page(row)}</td>
          <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{checked(row)}</td>
          <td className={`p-3 text-portal-small ${row.cityList ? '' : 'text-portal-muted'}`}>{cities(row)}</td>
        </tr>
      ))}
    </DataTable>
  );
}
